/**
 * Monaco Editor Language Configuration for MethodScript
 *
 * This module provides language configuration, syntax highlighting (Monarch tokenizer),
 * and theme definitions for MethodScript in Monaco Editor.
 *
 * Use this in all Monaco-based editors (WASM, Blazor, VSCode) to ensure consistent
 * syntax highlighting and language behavior.
 */

import { monarchTokensProvider } from './monarch-generated.js';

/**
 * Language configuration for MethodScript
 * Defines comments, brackets, auto-closing pairs, etc.
 */
export const languageConfig = {
    comments: {
        lineComment: '#',
    },
    brackets: [
        ['[', ']'],
        ['if', 'endif'],
        ['loop', 'endloop'],
        ['pck_start', 'pck_end'],
        // Measurement loop commands
        ['meas_loop_ca', 'endloop'],
        ['meas_loop_ca_alt_mux', 'endloop'],
        ['meas_loop_cv', 'endloop'],
        ['meas_loop_lsv', 'endloop'],
        ['meas_loop_dpv', 'endloop'],
        ['meas_loop_swv', 'endloop'],
        ['meas_loop_npv', 'endloop'],
        ['meas_loop_pad', 'endloop'],
        ['meas_loop_ocp', 'endloop'],
        ['meas_loop_ocp_alt_mux', 'endloop'],
        ['meas_loop_cp', 'endloop'],
        ['meas_loop_cp_alt_mux', 'endloop'],
        ['meas_loop_lsp', 'endloop'],
        ['meas_loop_acv', 'endloop'],
        ['meas_loop_eis', 'endloop'],
        ['meas_loop_eis_dual', 'endloop'],
        ['meas_loop_geis', 'endloop'],
    ] as Array<[string, string]>,
    autoClosingPairs: [
        { open: '[', close: ']' },
        { open: '"', close: '"' },
        { open: '(', close: ')' },
    ],
    surroundingPairs: [
        { open: '[', close: ']' },
        { open: '"', close: '"' },
        { open: '(', close: ')' },
    ],
    folding: {
        markers: {
            start: new RegExp('^\\s*#region\\b'),
            end: new RegExp('^\\s*#endregion\\b'),
        },
    },
};

/**
 * Monarch Tokenizer for MethodScript syntax highlighting
 * Generated from syntax-definitions.ts - DO NOT EDIT MANUALLY
 * Run `npm run generate-syntax` to regenerate
 */
export { monarchTokensProvider } from './monarch-generated.js';

/**
 * Theme definition for MethodScript
 * Defines colors for tokens in dark mode
 */
export const methodscriptTheme = {
    base: 'vs-dark' as const,
    inherit: true,
    rules: [
        { token: 'keyword', foreground: 'FFD700', fontStyle: 'bold' }, // Gold for control flow
        { token: 'keyword.measurement', foreground: 'FFD700', fontStyle: 'bold' }, // Gold for measurement loops
        { token: 'keyword.command', foreground: 'DCDCAA' }, // Light yellow for commands
        { token: 'type.identifier', foreground: '4EC9B0' }, // Teal for VarTypes
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'number.integer', foreground: 'B5CEA8' },
        { token: 'number.float', foreground: 'B5CEA8' },
        { token: 'number.si', foreground: 'B5CEA8' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'string.escape', foreground: 'D7BA7D' },
        { token: 'string.interpolation.delimiter', foreground: 'FFD700' }, // Gold for { } in f-strings
        { token: 'identifier.interpolation', foreground: '9CDCFE' }, // Blue for variables in interpolation
        { token: 'delimiter.bracket', foreground: 'FFD700' }, // Gold for [ ] in interpolation
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
        { token: 'operator', foreground: 'D4D4D4' },
        { token: 'identifier', foreground: '9CDCFE' },
    ],
    colors: {
        'editor.background': '#1E1E1E',
        'editor.foreground': '#D4D4D4',
        'editorLineNumber.foreground': '#858585',
        'editorCursor.foreground': '#AEAFAD',
    },
};

/**
 * Combined configuration object for easy import
 */
export const methodscriptConfig = {
    languageConfig,
    monarchTokensProvider,
    theme: methodscriptTheme,
};
